/**
 * Чистая логика лендинга без DOM: режимы и их серверы, онлайн по режимам, карточки привилегий
 * из товаров магазина, склонения. Её проверяет `npm test`.
 */

/**
 * Режимы лендинга. servers — как называются серверы режима в /api/v1/online: имя сервера
 * или его группа; у арен и вариантов к имени добавляется суффикс (skywars-3, anarchy-hybrid).
 */
export const MODES = [
  { id: "skyblock", title: "SkyBlock", servers: ["skyblock"] },
  { id: "anarchy", title: "Анархия", servers: ["anarchy"] },
  { id: "skywars", title: "SkyWars", servers: ["skywars"] },
  { id: "bedwars", title: "BedWars", servers: ["bedwars"] },
  { id: "buildbattle", title: "BuildBattle", servers: ["buildbattle"] },
  { id: "fastgames", title: "FastGames", servers: ["fastgames"] },
  { id: "arcade", title: "Arcade", servers: ["arcade", "tntrun", "dropper", "dragonescape", "gungame", "blockparty", "splegg"] },
];

/** Имя режима на сервере: группа, а без неё — имя сервера; в нижнем регистре. */
function serverMode(server) {
  return String((server && (server.group || server.name)) || "").toLowerCase();
}

/**
 * Онлайн по режимам лендинга: { skyblock: 12, anarchy: 0, … }. Серверы, которые не относятся
 * к режимам лендинга (хаб, Build), не учитываются.
 */
export function onlineByMode(servers) {
  const result = Object.fromEntries(MODES.map((mode) => [mode.id, 0]));
  for (const server of Array.isArray(servers) ? servers : []) {
    const name = serverMode(server);
    const mode = MODES.find((item) => item.servers.some((base) => name === base || name.startsWith(`${base}-`)));
    if (mode) result[mode.id] += Math.max(0, Number(server.online) || 0);
  }
  return result;
}

/** Цвета привилегий — как у групп в игре. */
const PRIVILEGE_COLORS = { VIP: "#4CAF50", MVP: "#00BCD4", MRP: "#E91E63", Sponsor: "#FFC107" };

/** Дней в месяце подписки: срок привилегии кратен ему. */
const MONTH_DAYS = 30;

/** Подписи сроков переключателя: месяц, 3 месяца, полгода; другой срок — «N дн.». */
const TERM_LABELS = { 30: "Месяц", 90: "3 месяца", 180: "Полгода" };

/**
 * Привилегии из товаров магазина (/api/v1/store/items): только привилегии (RANK) с ценой.
 * Название товара «VIP+ — 3 месяца» делится на имя «VIP» и плюс; сроки одной привилегии
 * собираются вместе по числу дней (durationDays, без него — месяц).
 *
 * @returns {Array<{name: string, plus: boolean, color: string|null, terms: Map<number, {id: number, price: number}>}>}
 *   по возрастанию цены месяца (без месячного срока — самого короткого)
 */
function privileges(items) {
  const byName = new Map();
  for (const item of Array.isArray(items) ? items : []) {
    if (!item || item.type !== "RANK" || !(Number(item.priceRub) > 0)) continue;
    const [title] = String(item.name || item.nameKey || "").split(/\s+[—–-]\s+/);
    const plus = title.endsWith("+");
    const name = plus ? title.slice(0, -1) : title;
    const key = `${name}${plus ? "+" : ""}`;
    if (!byName.has(key)) byName.set(key, { name, plus, color: PRIVILEGE_COLORS[name] || null, terms: new Map() });
    const days = Number(item.durationDays) > 0 ? Number(item.durationDays) : MONTH_DAYS;
    byName.get(key).terms.set(days, { id: item.id, price: Number(item.priceRub) });
  }
  const basePrice = (privilege) => (privilege.terms.get(MONTH_DAYS) || privilege.terms.get(Math.min(...privilege.terms.keys()))).price;
  return [...byName.values()].sort((a, b) => basePrice(a) - basePrice(b));
}

/**
 * Скидка срока против помесячной оплаты: 3 месяца по 199 ₽ стоят 597 ₽, за 499 ₽ — скидка 16 %.
 * Без месячной цены или у самого месяца — 0.
 */
function termDiscount(privilege, days) {
  const month = privilege.terms.get(MONTH_DAYS);
  const term = privilege.terms.get(days);
  if (!month || !term || days <= MONTH_DAYS) return 0;
  const full = (month.price * days) / MONTH_DAYS;
  return Math.max(0, Math.round((1 - term.price / full) * 100));
}

/**
 * Сроки переключателя: все сроки, которые есть у привилегий, по возрастанию, со скидкой —
 * наибольшей среди привилегий (переключатель показывает «до −N %», если скидки разные).
 *
 * @returns {Array<{days: number, label: string, discount: number, same: boolean}>}
 */
export function privilegeTerms(items) {
  const list = privileges(items);
  const days = [...new Set(list.flatMap((privilege) => [...privilege.terms.keys()]))].sort((a, b) => a - b);
  return days.map((term) => {
    const discounts = list.filter((privilege) => privilege.terms.has(term)).map((privilege) => termDiscount(privilege, term));
    const discount = Math.max(0, ...discounts);
    return {
      days: term,
      label: TERM_LABELS[term] || `${term} дн.`,
      discount,
      same: discounts.every((value) => value === discount),
    };
  });
}

/**
 * Карточки привилегий на выбранный срок: у каждой привилегии с этим сроком — его цена,
 * порядок — по цене месяца, чтобы карточки не прыгали при смене срока.
 *
 * @returns {Array<{id: number, name: string, plus: boolean, days: number, price: number, color: string|null}>}
 */
export function privilegeCards(items, days = MONTH_DAYS) {
  return privileges(items)
    .filter((privilege) => privilege.terms.has(days))
    .map((privilege) => {
      const term = privilege.terms.get(days);
      return { id: term.id, name: privilege.name, plus: privilege.plus, days, price: term.price, color: privilege.color };
    });
}

/** Подпись срока под ценой: «в месяц» для месяца, иначе «за 3 месяца», «за полгода». */
export function periodLabel(days) {
  if (!days || days === MONTH_DAYS) return "в месяц";
  return days === 180 ? "за полгода" : `за ${(TERM_LABELS[days] || `${days} дн.`).toLowerCase()}`;
}

/** Цена в рублях с пробелами между разрядами: 1999 → «1 999». */
export function formatRub(value) {
  return Math.round(value).toLocaleString("ru-RU");
}

/** Склонение по числу: plural(5, "игрок", "игрока", "игроков") → «игроков». */
export function plural(count, one, few, many) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
